import AcademicGenealogy from "./AcademicGenealogy";
import React from "react";
import {
    Box,
    Link,
    Typography,
    Container,
    Grid,
    Paper,
    Chip,
    Stack
} from "@mui/material";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WorkIcon from "@mui/icons-material/Work";
import GroupIcon from "@mui/icons-material/Group";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { info, singlePage } from "../../info/Info";
import { Helmet } from "react-helmet-async";

export default function About({ innerRef }) {
    return (
        <>
            {!singlePage && (
                <Helmet>
                    <title>About & Research | Filippos Kokkinos</title>
                    <meta
                        name="description"
                        content="About Filippos Kokkinos, AI Research Scientist at Meta. Research themes, news, experience, mentorship, and academic service."
                    />
                </Helmet>
            )}
            <Container maxWidth="md" sx={{ py: 4 }} ref={innerRef} id="about">
                {/* 1. Research Overview / Bio Card */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: "16px",
                        border: "1px solid",
                        borderColor: "divider",
                        background: (theme) =>
                            theme.palette.mode === "dark"
                                ? "rgba(30, 41, 59, 0.7)"
                                : "rgba(255, 255, 255, 0.9)",
                        backdropFilter: "blur(8px)",
                        mb: 4
                    }}
                >
                    <Typography
                        variant="h5"
                        component="h2"
                        fontWeight={700}
                        gutterBottom
                        display="flex"
                        alignItems="center"
                        gap={1.2}
                    >
                        <LightbulbIcon color="primary" /> Research Overview
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.75, fontSize: "1.02rem" }}>
                        I am an <strong>AI Research Scientist at Meta</strong> working on{" "}
                        <strong>Multimodal Foundation Models</strong>, <strong>Vision-Language Reasoning</strong>, and{" "}
                        <strong>3D/4D Generative AI</strong>. Over the past two years, I led research in STEM-focused multimodal reasoning and reinforcement learning post-training across Llama and next-gen model generations.
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.75, fontSize: "1.02rem" }}>
                        Prior to this, I contributed to flagship 3D generative efforts including{" "}
                        <strong>Meta 3D AssetGen</strong> and <strong>Meta 3D Gen</strong>, focusing on 3D generative model distillation and feed-forward reconstruction frameworks.
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.75, fontSize: "1.02rem" }}>
                        I earned my Ph.D. in Computer Science from <strong>University College London (UCL)</strong> under the supervision of{" "}
                        <strong>Prof. Iasonas Kokkinos</strong>, focusing on deep structured optimization for 2D and 3D computer vision.
                    </Typography>
                </Paper>

                {/* 2. Recent News Section */}
                <Box mb={5} id="news">
                    <Typography
                        variant="h5"
                        component="h2"
                        fontWeight={700}
                        gutterBottom
                        display="flex"
                        alignItems="center"
                        gap={1.2}
                    >
                        <NewspaperIcon color="primary" /> Recent News
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            borderRadius: "14px",
                            border: "1px solid",
                            borderColor: "divider",
                            background: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "rgba(30, 41, 59, 0.5)"
                                    : "rgba(255, 255, 255, 0.8)"
                        }}
                    >
                        <Stack spacing={1.8}>
                            {info.news.map((item, index) => (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="flex-start"
                                    gap={1.5}
                                    sx={{
                                        pb: index < info.news.length - 1 ? 1.5 : 0,
                                        borderBottom:
                                            index < info.news.length - 1
                                                ? "1px dashed"
                                                : "none",
                                        borderColor: "divider"
                                    }}
                                >
                                    <Chip
                                        label={item.date}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: "0.75rem",
                                            borderRadius: "6px",
                                            minWidth: "68px"
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ lineHeight: 1.6, flexGrow: 1 }}>
                                        {item.link ? (
                                            <Link
                                                href={item.link}
                                                target="_blank"
                                                rel="noopener"
                                                underline="hover"
                                                sx={{ color: "text.primary", "&:hover": { color: "primary.main" } }}
                                            >
                                                {item.text}
                                            </Link>
                                        ) : (
                                            item.text
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                </Box>

                {/* 3. Research Themes / Focus Pillars */}
                <Box mb={5} id="research">
                    <Typography
                        variant="h5"
                        component="h2"
                        fontWeight={700}
                        gutterBottom
                        display="flex"
                        alignItems="center"
                        gap={1.2}
                    >
                        <LightbulbIcon color="primary" /> Research Themes
                    </Typography>
                    <Grid container spacing={2.5}>
                        {info.researchThemes.map((theme, index) => (
                            <Grid item xs={12} key={index}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        borderRadius: "14px",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        background: (t) =>
                                            t.palette.mode === "dark"
                                                ? "rgba(30, 41, 59, 0.5)"
                                                : "rgba(255, 255, 255, 0.8)",
                                        transition: "transform 0.2s ease",
                                        "&:hover": { transform: "translateY(-2px)" }
                                    }}
                                >
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        {theme.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.6 }}>
                                        {theme.description}
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={0.8}>
                                        {theme.tags.map((tag, tIndex) => (
                                            <Chip
                                                key={tIndex}
                                                label={tag}
                                                size="small"
                                                variant="outlined"
                                                sx={{ borderRadius: "6px", fontSize: "0.75rem" }}
                                            />
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 4. Experience & Education Timeline */}
                <Box mb={5} id="experience">
                    <Typography
                        variant="h5"
                        component="h2"
                        fontWeight={700}
                        gutterBottom
                        display="flex"
                        alignItems="center"
                        gap={1.2}
                    >
                        <WorkIcon color="primary" /> Experience & Education
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            borderRadius: "14px",
                            border: "1px solid",
                            borderColor: "divider",
                            background: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "rgba(30, 41, 59, 0.5)"
                                    : "rgba(255, 255, 255, 0.8)"
                        }}
                    >
                        <Stack spacing={2.5}>
                            {info.experience.map((exp, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        pb: index < info.experience.length - 1 ? 2 : 0,
                                        borderBottom:
                                            index < info.experience.length - 1
                                                ? "1px dashed"
                                                : "none",
                                        borderColor: "divider"
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1}>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {exp.role} &middot; <span style={{ color: "#3b82f6" }}>{exp.org}</span>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            {exp.period} | {exp.location}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55 }}>
                                        {exp.description}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                </Box>

                {/* 5. Mentorship & Students */}
                <Box mb={5} id="mentorship">
                    <Grid container spacing={3}>
                        {/* Supervised Students */}
                        <Grid item xs={12} sm={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    height: "100%",
                                    borderRadius: "14px",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    background: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "rgba(30, 41, 59, 0.5)"
                                            : "rgba(255, 255, 255, 0.8)"
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    gutterBottom
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <GroupIcon color="primary" /> Supervised Students & Interns
                                </Typography>
                                <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 1.5, "& li": { py: 0.35 } }}>
                                    {info.students.map((student, index) => (
                                        <li key={index}>
                                            <Link
                                                href={student.link}
                                                target="_blank"
                                                rel="noopener"
                                                underline="hover"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: "text.primary",
                                                    "&:hover": { color: "primary.main" }
                                                }}
                                            >
                                                {student.name}
                                            </Link>
                                        </li>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Mentors */}
                        <Grid item xs={12} sm={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    height: "100%",
                                    borderRadius: "14px",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    background: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "rgba(30, 41, 59, 0.5)"
                                            : "rgba(255, 255, 255, 0.8)"
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    gutterBottom
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <SchoolIcon color="primary" /> Academic Mentors
                                </Typography>
                                <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 1.5, "& li": { py: 0.35 } }}>
                                    {info.mentors.map((mentor, index) => (
                                        <li key={index}>
                                            <Link
                                                href={mentor.link}
                                                target="_blank"
                                                rel="noopener"
                                                underline="hover"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: "text.primary",
                                                    "&:hover": { color: "primary.main" }
                                                }}
                                            >
                                                {mentor.name}
                                            </Link>
                                        </li>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Academic Lineage & Genealogy */}
                    <Box sx={{ mt: 3 }}>
                        <AcademicGenealogy />
                    </Box>
                </Box>

                {/* 6. Academic Services & Reviewing */}
                <Box id="service">
                    <Typography
                        variant="h5"
                        component="h2"
                        fontWeight={700}
                        gutterBottom
                        display="flex"
                        alignItems="center"
                        gap={1.2}
                    >
                        <AssignmentTurnedInIcon color="primary" /> Academic Services & Reviewing
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            borderRadius: "14px",
                            border: "1px solid",
                            borderColor: "divider",
                            background: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "rgba(30, 41, 59, 0.5)"
                                    : "rgba(255, 255, 255, 0.8)"
                        }}
                    >
                        <Stack spacing={2}>
                            {info.services.map((svc, index) => (
                                <Box key={index}>
                                    <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                                        {svc.category}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                                        {svc.venues.join(" · ")}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                </Box>
            </Container>
        </>
    );
}
